import { faRoute } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useAxiosHook from '../../../hooks/useAxiosHook';
import { useEndpointGuard } from '../../../hooks/useEndpointGuard';
import usePageTitle from '../../../hooks/usePageTitle';
import useRouteDialog from '../../../hooks/useRouteDialog';
import useTableColumns from '../../../hooks/useTableColumns';
import useTableState from '../../../hooks/useTableState';
import { WithPagination } from '../../../types/api';
import * as logisticsRoutes from '../../../utils/constants/auth/logisticsRoutes';
import {
  RoutePaths,
  constructIdRoute,
} from '../../../utils/constants/routePaths';
import { sequential } from '../../../utils/helpers/functions';
import { queryString } from '../../../utils/helpers/http';
import { httpQueryObject } from '../../../utils/helpers/misc';
import Table from '../../DataTable/Table/Table';
import Flex from '../../layout/flex/Flex';
import MainContent from '../../layout/flex/MainContent';
import Sidebar from '../../layout/flex/Sidebar';
import Filters from '../Components/Filters/Filters';
import HeaderPages from '../Components/HeaderPages/HeaderPages';
import CreateDialog from './Dialogs/Create/CreateDialog';
import DeleteDialog from './Dialogs/Delete/DeleteDialog';
import ViewDialog from './Dialogs/View/ViewDialog';
import {
  LogisticsRoute,
  additionalColumnProperties,
  getColumnHeadersMap,
  tableStorageKey,
} from './LogisticsRoutes.functions';
import useTableFilters from './useTableFilters';

function LogisticsRoutes(): JSX.Element {
  const { t } = useTranslation();
  usePageTitle(t('Logistics routes'));
  const [action, setAction] = useState<string>('');
  const [caller, setCaller] =
    useState<'group-actions' | 'context-menu'>('context-menu');
  const readGuard = useEndpointGuard(logisticsRoutes.resourceLogisticRoutes);
  const createGuard = useEndpointGuard(logisticsRoutes.createLogisticRoutes);
  const editGuard = useEndpointGuard(logisticsRoutes.editLogisticRoutes);
  const deleteGuard = useEndpointGuard(logisticsRoutes.deleteLogisticRoutes);

  const {
    tableRef,
    page,
    setPage,
    limit,
    setLimit,
    sortField,
    sortOrder,
    selection,
    selectionMultiple,
    setSortField,
    setSortOrder,
    setSelection,
    setSelectionMultiple,
    contextMenuSelection,
    setContextMenuSelection,
  } = useTableState<LogisticsRoute>(tableStorageKey);

  const { filters, headerFiltersCount, resetAllFilters, httpFiltersObj } =
    useTableFilters(page, setPage!, limit);

  const columnHeadersMap = useMemo(() => getColumnHeadersMap(t), [t]);

  const {
    data,
    error,
    isLoading,
    reload: reloadCollection,
  } = useAxiosHook<WithPagination<LogisticsRoute[]>>(
    '/logistics-route' + queryString(httpQueryObject(httpFiltersObj))
  );

  const {
    show: showViewDialog,
    hide: hideViewDialog,
    isVisible: isViewDialogVisible,
  } = useRouteDialog(
    RoutePaths.LogisticsRoutesPage,
    constructIdRoute(RoutePaths.ViewLogisticsRoutes, contextMenuSelection?.id),
    readGuard
  );

  const {
    show: showCreateDialog,
    hide: hideCreateDialog,
    isVisible: isCreateDialogVisible,
  } = useRouteDialog(
    RoutePaths.LogisticsRoutesPage,
    RoutePaths.CreateLogisticsRoutes,
    createGuard
  );

  const {
    show: showEditDialog,
    hide: hideEditDialog,
    isVisible: isEditDialogVisible,
  } = useRouteDialog(
    RoutePaths.LogisticsRoutesPage,
    constructIdRoute(RoutePaths.EditLogisticsRoutes, contextMenuSelection?.id),
    editGuard
  );

  const {
    show: showDeleteDialog,
    hide: hideDeleteDialog,
    isVisible: isDeleteDialogVisible,
  } = useRouteDialog(
    RoutePaths.LogisticsRoutesPage,
    constructIdRoute(
      RoutePaths.DeleteLogisticsRoutes,
      contextMenuSelection?.id
    ),
    deleteGuard
  );

  const { selectedColumns, setSelectedColumns, columnOptions, columns } =
    useTableColumns(
      page,
      limit,
      'clients',
      columnHeadersMap,
      columnHeadersMap,
      (c) =>
        additionalColumnProperties(
          t,
          c as keyof typeof columnHeadersMap,
          setContextMenuSelection,
          setAction,
          setCaller,
          readGuard,
          editGuard,
          deleteGuard
        )
    );

  const finalColumns = useMemo<JSX.Element[]>(
    () => [
      ...columns,
      <Column
        key="action-column"
        header={t('Actions')}
        field="actions"
        frozen
        alignFrozen="right"
        {...additionalColumnProperties(
          t,
          'actions',
          setContextMenuSelection,
          setAction,
          setCaller,
          readGuard,
          editGuard,
          deleteGuard
        )}
      />,
    ],
    [
      columns,
      t,
      setContextMenuSelection,
      setAction,
      setCaller,
      readGuard,
      editGuard,
      deleteGuard,
    ]
  );

  useEffect(() => {
    if (action && contextMenuSelection) {
      setCaller('context-menu');

      if (action === 'view-details') {
        showViewDialog();
      }
      if (action === 'edit') {
        showEditDialog();
      }
      if (action === 'delete') {
        showDeleteDialog();
      }

      setAction('');
    }
  }, [
    action,
    contextMenuSelection,
    showViewDialog,
    showEditDialog,
    showDeleteDialog,
    setAction,
  ]);

  function handleCreateLogisticsRoute() {
    setContextMenuSelection(undefined);
    showCreateDialog();
  }

  function handelEditButton(data: LogisticsRoute) {
    setContextMenuSelection(data);
    showEditDialog();
  }

  return (
    <div className="page logistics-routes-page">
      <HeaderPages
        title={t('Logistics routes')}
        subtitle={t('Logistics routes management')}
        icon={faRoute}
      >
        {createGuard && (
          <div>
            <Button
              label={t('Create route')}
              icon="fas fa-plus"
              className="main-btn"
              onClick={handleCreateLogisticsRoute}
              data-cy="create-btn"
            />
          </div>
        )}
      </HeaderPages>

      <ViewDialog
        isShown={isViewDialogVisible}
        onHide={hideViewDialog}
        isEditBtnShown
        setIsEditDialog={handelEditButton}
      />

      <CreateDialog
        isEditDialog={isEditDialogVisible}
        isShown={isCreateDialogVisible || isEditDialogVisible}
        onHide={() => sequential(hideEditDialog, hideCreateDialog)}
        data={contextMenuSelection}
        reloadCollection={reloadCollection}
      />

      <DeleteDialog
        visible={isDeleteDialogVisible}
        data={
          caller === 'context-menu'
            ? contextMenuSelection
              ? [contextMenuSelection]
              : []
            : selectionMultiple
        }
        onHide={() => hideDeleteDialog()}
        reloadLogisticsRoute={reloadCollection}
        setSelectionMultiple={setSelectionMultiple}
        setContextMenuSelection={setContextMenuSelection}
      />

      <Flex direction="column">
        <Sidebar>
          <Filters
            filters={filters}
            resetAllFilters={resetAllFilters}
            filterHeight={180}
          />
        </Sidebar>
        <MainContent>
          <Table
            tableRef={tableRef}
            columns={finalColumns}
            data={data}
            isLoading={isLoading}
            hasError={!!error}
            reload={reloadCollection}
            headerTitle=""
            filterHeight={180}
            headerFiltersCount={headerFiltersCount}
            onHeaderFiltersResetAllBtnClick={resetAllFilters}
            setPage={setPage}
            setLimit={setLimit}
            sortField={sortField}
            rows={limit}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            setSelection={setSelection}
            sortOrder={sortOrder}
            selection={selection}
            storageString={tableStorageKey}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            columnOptions={columnOptions}
            contextMenuSelection={contextMenuSelection}
            setContextMenuSelection={setContextMenuSelection}
          />
        </MainContent>
      </Flex>
    </div>
  );
}

export default LogisticsRoutes;
